import { Money } from '../components/ui/Money';
import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import { toast } from '../hooks/use-toast';
import { Plus, Edit, Trash2, Download, Search, Package, TrendingDown, Percent, ArrowRightLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CATEGORIES = {
  "Vehicles": 25, "Computers & Software": 33.3, "Furniture & Fixtures": 10,
  "Real Estate / Property": 4, "Machinery & Equipment": 15, "Office Equipment": 20, "Other": 10,
};

const PURCHASE_CONDITIONS = ["New", "Used", "Refurbished"];

export default function CompanyAssets() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer' || user.role === 'report_viewer';

  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [conditions, setConditions] = useState(["In Use", "Kept in Warehouse", "Damaged", "Scraped", "Sold"]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterCondition, setFilterCondition] = useState('all');
  const [filterCompany, setFilterCompany] = useState('all');

  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(null);
  const [rateForm, setRateForm] = useState({ rate: '', effective_date: '', note: '' });
  const [showAddCondition, setShowAddCondition] = useState(false);
  const [newCondition, setNewCondition] = useState('');
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompany, setNewCompany] = useState('');
  const [showTransferDialog, setShowTransferDialog] = useState(null);
  const [transferForm, setTransferForm] = useState({ owning_company: '', note: '', date: new Date().toISOString().split('T')[0] });
  const [showExportDialog, setShowExportDialog] = useState(null); // 'pdf' or 'excel'
  const [exportCompany, setExportCompany] = useState('all');

  const emptyForm = {
    asset_name: '', category: 'Other', description: '', purchase_date: '',
    quantity: 1, unit_price: '', include_vat: false, vat_percent: 5,
    condition_at_purchase: 'New', current_condition: 'In Use',
    assigned_to_type: 'none', assigned_to_employee_id: '', assigned_to_name: '',
    depreciation_rate: 10, notes: '', owning_company: ''
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [assetsRes, empRes, catRes, condRes, compRes] = await Promise.all([
        axios.get(`${API}/api/company-assets`),
        axios.get(`${API}/api/employees`),
        axios.get(`${API}/api/company-assets/categories`),
        axios.get(`${API}/api/company-assets/conditions`),
        axios.get(`${API}/api/company-assets/companies`),
      ]);
      setAssets(assetsRes.data);
      setEmployees(empRes.data);
      setCategories(catRes.data);
      setConditions(condRes.data);
      setCompanies(compRes.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const filteredAssets = useMemo(() => {
    return assets.filter(a => {
      const matchSearch = !search || a.asset_name?.toLowerCase().includes(search.toLowerCase()) ||
        a.asset_code?.toLowerCase().includes(search.toLowerCase()) ||
        a.assigned_to_name?.toLowerCase().includes(search.toLowerCase());
      const matchCat = filterCategory === 'all' || a.category === filterCategory;
      const matchCond = filterCondition === 'all' || a.current_condition === filterCondition;
      const matchComp = filterCompany === 'all' || a.owning_company === filterCompany;
      return matchSearch && matchCat && matchCond && matchComp;
    });
  }, [assets, search, filterCategory, filterCondition, filterCompany]);

  const totals = useMemo(() => ({
    count: filteredAssets.length,
    purchase: filteredAssets.reduce((s, a) => s + (a.purchase_value || 0), 0),
    current: filteredAssets.reduce((s, a) => s + (a.current_value || 0), 0),
    depreciation: filteredAssets.reduce((s, a) => s + (a.total_depreciation || 0), 0),
  }), [filteredAssets]);

  const openAdd = () => { setEditingAsset(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (asset) => {
    setEditingAsset(asset);
    setForm({
      asset_name: asset.asset_name || '', category: asset.category || 'Other',
      description: asset.description || '', purchase_date: asset.purchase_date || '',
      quantity: asset.quantity || 1,
      unit_price: asset.unit_price || asset.purchase_value || '',
      include_vat: asset.include_vat || false,
      vat_percent: asset.vat_percent ?? 5,
      condition_at_purchase: asset.condition_at_purchase || 'New',
      current_condition: asset.current_condition || 'In Use',
      assigned_to_type: asset.assigned_to_type || 'none',
      assigned_to_employee_id: asset.assigned_to_employee_id || '',
      assigned_to_name: asset.assigned_to_name || '',
      depreciation_rate: asset.depreciation_rate || 10, notes: asset.notes || '',
      owning_company: asset.owning_company || ''
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.asset_name || !form.purchase_date || !form.unit_price) {
      toast({ title: 'Error', description: 'Name, date, and unit price are required', variant: 'destructive' });
      return;
    }
    try {
      if (editingAsset) {
        await axios.put(`${API}/api/company-assets/${editingAsset.id}`, form);
        toast({ title: 'Success', description: 'Asset updated' });
      } else {
        await axios.post(`${API}/api/company-assets`, form);
        toast({ title: 'Success', description: 'Asset created' });
      }
      setShowForm(false);
      loadAll();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save asset', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/company-assets/${deleteConfirm.id}`);
      toast({ title: 'Deleted', description: 'Asset removed' });
      loadAll();
    } catch { toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' }); }
    finally { setDeleting(false); setDeleteConfirm(null); }
  };

  const handleUpdateRate = async () => {
    if (!showRateDialog || !rateForm.rate || !rateForm.effective_date) return;
    try {
      await axios.put(`${API}/api/company-assets/${showRateDialog.id}/depreciation-rate`, rateForm);
      toast({ title: 'Success', description: 'Depreciation rate updated' });
      setShowRateDialog(null);
      loadAll();
    } catch { toast({ title: 'Error', description: 'Failed to update rate', variant: 'destructive' }); }
  };

  const handleAddCondition = async () => {
    if (!newCondition.trim()) return;
    try {
      await axios.post(`${API}/api/company-assets/conditions`, { name: newCondition.trim() });
      setConditions(prev => [...prev, newCondition.trim()]);
      setForm(prev => ({ ...prev, current_condition: newCondition.trim() }));
      setNewCondition('');
      setShowAddCondition(false);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleAddCompany = async () => {
    if (!newCompany.trim()) return;
    try {
      await axios.post(`${API}/api/company-assets/companies`, { name: newCompany.trim() });
      setCompanies(prev => [...prev, newCompany.trim()]);
      setForm(prev => ({ ...prev, owning_company: newCompany.trim() }));
      setNewCompany('');
      setShowAddCompany(false);
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleTransfer = async () => {
    if (!showTransferDialog || !transferForm.owning_company) return;
    try {
      const userName = user.name || user.full_name || user.username || 'Unknown';
      await axios.put(`${API}/api/company-assets/${showTransferDialog.id}/transfer`, {
        ...transferForm,
        transferred_by: userName
      });
      toast({ title: 'Success', description: `Asset transferred to ${transferForm.owning_company}` });
      setShowTransferDialog(null);
      setTransferForm({ owning_company: '', note: '', date: new Date().toISOString().split('T')[0] });
      loadAll();
    } catch { toast({ title: 'Error', description: 'Transfer failed', variant: 'destructive' }); }
  };

  const handleExport = async () => {
    if (!showExportDialog) return;
    try {
      const params = exportCompany !== 'all' ? `?company=${encodeURIComponent(exportCompany)}` : '';
      const res = await fetch(`${API}/api/company-assets/export/${showExportDialog}${params}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const compLabel = exportCompany !== 'all' ? `_${exportCompany.replace(/\s+/g, '_')}` : '';
      a.download = `Company_Assets${compLabel}.${showExportDialog === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      setShowExportDialog(null);
    } catch { toast({ title: 'Error', description: 'Export failed', variant: 'destructive' }); }
  };

  const fmtNum = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6" data-testid="company-assets-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Package className="h-6 w-6" /> Company Assets</h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { setExportCompany('all'); setShowExportDialog('pdf'); }} data-testid="export-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setExportCompany('all'); setShowExportDialog('excel'); }} data-testid="export-excel-btn">
            <Download className="h-4 w-4 mr-1" /> Excel
          </Button>
          {!isViewer && (
            <Button onClick={openAdd} data-testid="add-asset-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Asset
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <p className="text-sm text-gray-500">Total Assets</p>
          <p className="text-2xl font-bold">{totals.count}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-sm text-gray-500">Purchase Value</p>
          <p className="text-xl font-bold text-blue-600"><Money value={totals.purchase} /></p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-sm text-gray-500">Current Value</p>
          <p className="text-xl font-bold text-green-600"><Money value={totals.current} /></p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-sm text-gray-500">Total Depreciation</p>
          <p className="text-xl font-bold text-red-600"><Money value={totals.depreciation} /></p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search assets..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="search-assets" />
        </div>
        <Select value={filterCompany} onValueChange={setFilterCompany}>
          <SelectTrigger className="w-[180px]" data-testid="filter-company"><SelectValue placeholder="Company" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.keys(categories).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterCondition} onValueChange={setFilterCondition}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Condition" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Conditions</SelectItem>
            {conditions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Assets Table */}
      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left p-3 font-medium">Code</th>
                <th className="text-left p-3 font-medium">Asset Name</th>
                <th className="text-left p-3 font-medium">Company</th>
                <th className="text-left p-3 font-medium">Category</th>
                <th className="text-center p-3 font-medium">Qty</th>
                <th className="text-right p-3 font-medium">Unit Price</th>
                <th className="text-right p-3 font-medium">Total Value</th>
                <th className="text-center p-3 font-medium">Dep. Rate</th>
                <th className="text-right p-3 font-medium">Current Value</th>
                <th className="text-left p-3 font-medium">Condition</th>
                <th className="text-left p-3 font-medium">Assigned To</th>
                {!isViewer && <th className="text-center p-3 font-medium">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredAssets.length === 0 ? (
                <tr><td colSpan={isViewer ? 11 : 12} className="text-center py-8 text-gray-500">No assets found</td></tr>
              ) : filteredAssets.map(asset => {
                const depPercent = asset.purchase_value > 0 ? ((asset.total_depreciation / asset.purchase_value) * 100).toFixed(1) : 0;
                return (
                  <tr key={asset.id} className="border-b hover:bg-gray-50" data-testid={`asset-row-${asset.id}`}>
                    <td className="p-3 font-mono text-xs">{asset.asset_code}</td>
                    <td className="p-3">
                      <div className="font-medium">{asset.asset_name}</div>
                      {asset.description && <div className="text-xs text-gray-500 truncate max-w-[200px]">{asset.description}</div>}
                    </td>
                    <td className="p-3">
                      {asset.owning_company ? (
                        <Badge variant="outline" className="font-medium">{asset.owning_company}</Badge>
                      ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                      )}
                    </td>
                    <td className="p-3"><Badge variant="outline">{asset.category}</Badge></td>
                    <td className="p-3 text-center">{asset.quantity || 1}</td>
                    <td className="p-3 text-right text-sm">{fmtNum(asset.unit_price || asset.purchase_value)}</td>
                    <td className="p-3 text-right font-medium">{fmtNum(asset.purchase_value)}</td>
                    <td className="p-3 text-center">
                      <span className="text-xs">{asset.depreciation_rate}%</span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="font-medium text-green-700">{fmtNum(asset.current_value)}</div>
                      <div className="text-xs text-red-500">-{fmtNum(asset.total_depreciation)} ({depPercent}%)</div>
                    </td>
                    <td className="p-3">
                      <Badge className={
                        asset.current_condition === 'In Use' ? 'bg-green-100 text-green-700' :
                        asset.current_condition === 'Damaged' ? 'bg-red-100 text-red-700' :
                        asset.current_condition === 'Sold' ? 'bg-blue-100 text-blue-700' :
                        asset.current_condition === 'Scraped' ? 'bg-gray-200 text-gray-700' :
                        'bg-yellow-100 text-yellow-700'
                      }>{asset.current_condition}</Badge>
                    </td>
                    <td className="p-3 text-sm">{asset.assigned_to_name || 'Unassigned'}</td>
                    {!isViewer && (
                      <td className="p-3">
                        <div className="flex gap-1 justify-center">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(asset)} title="Edit">
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Transfer Company"
                            onClick={() => { setShowTransferDialog(asset); setTransferForm({ owning_company: '', note: '', date: new Date().toISOString().split('T')[0] }); }}>
                            <ArrowRightLeft className="h-3.5 w-3.5 text-blue-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Change Rate"
                            onClick={() => { setShowRateDialog(asset); setRateForm({ rate: asset.depreciation_rate, effective_date: new Date().toISOString().split('T')[0], note: '' }); }}>
                            <Percent className="h-3.5 w-3.5 text-orange-600" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => setDeleteConfirm(asset)} title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Asset Name *</Label>
                <Input value={form.asset_name} onChange={e => setForm(p => ({ ...p, asset_name: e.target.value }))}
                  required data-testid="asset-name-input" />
              </div>
              <div>
                <Label>Owning Company</Label>
                {!showAddCompany ? (
                  <Select value={form.owning_company || '__none__'} onValueChange={v => {
                    if (v === '__add_new__') { setShowAddCompany(true); }
                    else if (v === '__none__') { setForm(p => ({ ...p, owning_company: '' })); }
                    else { setForm(p => ({ ...p, owning_company: v })); }
                  }}>
                    <SelectTrigger data-testid="asset-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- None --</SelectItem>
                      {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="__add_new__" className="text-purple-600 font-medium">+ Add New Company</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2">
                    <Input value={newCompany} onChange={e => setNewCompany(e.target.value)} placeholder="Company name..." className="flex-1"
                      data-testid="new-company-input" />
                    <Button type="button" size="sm" onClick={handleAddCompany} data-testid="save-new-company-btn">Add</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => { setShowAddCompany(false); setNewCompany(''); }}>Cancel</Button>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={v => {
                  const rate = categories[v] || 10;
                  setForm(p => ({ ...p, category: v, depreciation_rate: rate }));
                }}>
                  <SelectTrigger data-testid="asset-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(categories).map(c => <SelectItem key={c} value={c}>{c} ({categories[c]}%)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  data-testid="asset-description-input" />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Purchase Date *</Label>
                <Input type="date" value={form.purchase_date} onChange={e => setForm(p => ({ ...p, purchase_date: e.target.value }))}
                  required data-testid="asset-purchase-date" />
              </div>
              <div>
                <Label>Quantity</Label>
                <Input type="number" min="1" value={form.quantity}
                  onChange={e => setForm(p => ({ ...p, quantity: parseInt(e.target.value) || 1 }))}
                  data-testid="asset-quantity" />
              </div>
              <div>
                <Label>Unit Price (AED) *</Label>
                <Input type="number" step="0.01" min="0" value={form.unit_price}
                  onChange={e => setForm(p => ({ ...p, unit_price: e.target.value }))}
                  required data-testid="asset-unit-price" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>VAT</Label>
                  <div className="flex items-center gap-1.5">
                    <Checkbox id="include-vat" checked={form.include_vat}
                      onCheckedChange={v => setForm(p => ({ ...p, include_vat: v }))}
                      data-testid="include-vat-checkbox" />
                    <label htmlFor="include-vat" className="text-xs text-muted-foreground cursor-pointer select-none">Include VAT</label>
                  </div>
                </div>
                {form.include_vat ? (
                  <div className="flex gap-1.5 items-center">
                    <Input type="number" step="0.1" min="0" max="100"
                      value={form.vat_percent}
                      onChange={e => setForm(p => ({ ...p, vat_percent: parseFloat(e.target.value) || 0 }))}
                      className="w-20" data-testid="vat-percent-input" />
                    <span className="text-sm text-muted-foreground">%</span>
                    <span className="text-xs text-muted-foreground ml-1">
                      = AED {(() => {
                        const subtotal = (parseFloat(form.unit_price) || 0) * (parseInt(form.quantity) || 1);
                        return (subtotal * (parseFloat(form.vat_percent) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      })()}
                    </span>
                  </div>
                ) : (
                  <Input type="text" readOnly disabled value="No VAT" className="bg-muted text-muted-foreground" />
                )}
              </div>
            </div>
            <div className="bg-gray-50 rounded-md px-4 py-2 flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {(() => {
                  const subtotal = (parseFloat(form.unit_price) || 0) * (parseInt(form.quantity) || 1);
                  const vatAmt = form.include_vat ? subtotal * (parseFloat(form.vat_percent) || 0) / 100 : 0;
                  return `Subtotal: AED ${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}${form.include_vat ? ` + VAT ${form.vat_percent}%: AED ${vatAmt.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : ''}`;
                })()}
              </span>
              <span className="text-base font-bold text-green-700" data-testid="asset-total-value">
                Total: AED {(() => {
                  const subtotal = (parseFloat(form.unit_price) || 0) * (parseInt(form.quantity) || 1);
                  const vatAmt = form.include_vat ? subtotal * (parseFloat(form.vat_percent) || 0) / 100 : 0;
                  return (subtotal + vatAmt).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                })()}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Depreciation Rate (%)</Label>
                <Input type="number" step="0.1" min="0" max="100" value={form.depreciation_rate}
                  onChange={e => setForm(p => ({ ...p, depreciation_rate: parseFloat(e.target.value) || 0 }))}
                  data-testid="asset-dep-rate" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Condition at Purchase</Label>
                <Select value={form.condition_at_purchase} onValueChange={v => setForm(p => ({ ...p, condition_at_purchase: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PURCHASE_CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Current Condition</Label>
                {!showAddCondition ? (
                  <Select value={form.current_condition} onValueChange={v => {
                    if (v === '__add_new__') { setShowAddCondition(true); } else { setForm(p => ({ ...p, current_condition: v })); }
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {conditions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="__add_new__" className="text-purple-600 font-medium">+ Add Custom Status</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2">
                    <Input value={newCondition} onChange={e => setNewCondition(e.target.value)} placeholder="New status..." className="flex-1" />
                    <Button type="button" size="sm" onClick={handleAddCondition}>Add</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => { setShowAddCondition(false); setNewCondition(''); }}>Cancel</Button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Assign To</Label>
              <Select value={form.assigned_to_type} onValueChange={v => setForm(p => ({ ...p, assigned_to_type: v, assigned_to_employee_id: '', assigned_to_name: '' }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="external">External Person</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.assigned_to_type === 'employee' && (
              <div>
                <Label>Select Employee</Label>
                <Select value={form.assigned_to_employee_id} onValueChange={v => {
                  const emp = employees.find(e => e.id === v);
                  setForm(p => ({ ...p, assigned_to_employee_id: v, assigned_to_name: emp?.name || '' }));
                }}>
                  <SelectTrigger data-testid="assign-employee-select"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.filter(e => e.employment_status === 'Active').map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.assigned_to_type === 'external' && (
              <div>
                <Label>External Person Name</Label>
                <Input value={form.assigned_to_name} onChange={e => setForm(p => ({ ...p, assigned_to_name: e.target.value }))}
                  placeholder="Enter name" data-testid="external-person-input" />
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-asset-btn">{editingAsset ? 'Update' : 'Create'} Asset</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer Company Dialog */}
      <Dialog open={!!showTransferDialog} onOpenChange={() => setShowTransferDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-blue-600" /> Transfer Asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Asset: <span className="font-medium">{showTransferDialog?.asset_name}</span></p>
            <p className="text-sm text-gray-600">Current Company: <span className="font-medium">{showTransferDialog?.owning_company || 'Not Set'}</span></p>
            {showTransferDialog?.transfer_history?.length > 0 && (
              <div className="bg-gray-50 rounded p-2 max-h-[140px] overflow-y-auto">
                <p className="text-xs font-medium text-gray-500 mb-1">Transfer History:</p>
                {showTransferDialog.transfer_history.map((h, i) => (
                  <div key={i} className="text-xs text-gray-600 py-1 border-b last:border-0 border-gray-200">
                    <div className="flex justify-between">
                      <span className="font-medium">{h.from_company || 'N/A'} &rarr; {h.to_company}</span>
                      <span className="text-gray-400">{h.date}</span>
                    </div>
                    <div className="flex justify-between text-gray-400 mt-0.5">
                      {h.transferred_by && <span>By: {h.transferred_by}</span>}
                      {h.note && <span>{h.note}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label>Transfer To Company *</Label>
              <Select value={transferForm.owning_company} onValueChange={v => setTransferForm(p => ({ ...p, owning_company: v }))}>
                <SelectTrigger data-testid="transfer-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  {companies.filter(c => c !== showTransferDialog?.owning_company).map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Transfer Date *</Label>
              <Input type="date" value={transferForm.date}
                onChange={e => setTransferForm(p => ({ ...p, date: e.target.value }))}
                data-testid="transfer-date-input" />
            </div>
            <div>
              <Label>Note</Label>
              <Input value={transferForm.note} onChange={e => setTransferForm(p => ({ ...p, note: e.target.value }))}
                placeholder="Reason for transfer" data-testid="transfer-note-input" />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setShowTransferDialog(null)}>Cancel</Button>
              <Button onClick={handleTransfer} disabled={!transferForm.owning_company || !transferForm.date}
                className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-transfer-btn">Transfer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Dialog - Company Selection */}
      <Dialog open={!!showExportDialog} onOpenChange={() => setShowExportDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="h-5 w-5" /> Export Assets</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Select the company whose assets you want to export as <span className="font-semibold uppercase">{showExportDialog}</span>:</p>
            <Select value={exportCompany} onValueChange={setExportCompany}>
              <SelectTrigger data-testid="export-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setShowExportDialog(null)}>Cancel</Button>
              <Button onClick={handleExport} data-testid="confirm-export-btn">Download</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Depreciation Rate Dialog */}
      <Dialog open={!!showRateDialog} onOpenChange={() => setShowRateDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><TrendingDown className="h-5 w-5 text-orange-600" /> Change Depreciation Rate</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Asset: <span className="font-medium">{showRateDialog?.asset_name}</span></p>
            <p className="text-sm text-gray-600">Current Rate: <span className="font-medium">{showRateDialog?.depreciation_rate}%</span></p>
            {showRateDialog?.depreciation_rate_history?.length > 0 && (
              <div className="bg-gray-50 rounded p-2 max-h-[120px] overflow-y-auto">
                <p className="text-xs font-medium text-gray-500 mb-1">Rate History:</p>
                {showRateDialog.depreciation_rate_history.map((h, i) => (
                  <div key={i} className="text-xs text-gray-600 flex justify-between">
                    <span>{h.rate}%</span>
                    <span>{h.effective_date}</span>
                    {h.note && <span className="text-gray-400 ml-1">({h.note})</span>}
                  </div>
                ))}
              </div>
            )}
            <div>
              <Label>New Rate (%)</Label>
              <Input type="number" step="0.1" min="0" max="100" value={rateForm.rate}
                onChange={e => setRateForm(p => ({ ...p, rate: parseFloat(e.target.value) || 0 }))} data-testid="new-rate-input" />
            </div>
            <div>
              <Label>Effective Date *</Label>
              <Input type="date" value={rateForm.effective_date}
                onChange={e => setRateForm(p => ({ ...p, effective_date: e.target.value }))} required data-testid="rate-effective-date" />
            </div>
            <div>
              <Label>Note</Label>
              <Input value={rateForm.note} onChange={e => setRateForm(p => ({ ...p, note: e.target.value }))} placeholder="Reason for change" />
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setShowRateDialog(null)}>Cancel</Button>
              <Button onClick={handleUpdateRate} className="bg-orange-600 hover:bg-orange-700" data-testid="save-rate-btn">Update Rate</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}
        title="Delete Asset" description="Are you sure you want to delete this asset?"
        itemName={deleteConfirm?.asset_name} onConfirm={handleDelete} loading={deleting} />
    </div>
  );
}
